const formatTime = (time: any): string => {
  if (!time) return '';

  // Normalize time input
  let lowerTime = '';
  if (time.trim()) {
    lowerTime = time.trim().toLowerCase();
  }

  // Match time parts (HH, MM) and optional AM/PM
  const timeMatch = lowerTime.match(/^(\d{1,2}):?(\d{2})?\s*(am|pm)?$/);

  if (!timeMatch) return '';

  let [_, hoursStr, minutesStr, meridian] = timeMatch;

  let hours = parseInt(hoursStr, 10);
  const minutes = minutesStr ? parseInt(minutesStr, 10) : 0;

  if (isNaN(hours) || hours < 0 || hours > 12) return ''; // Check if hours are valid

  if (meridian) {
    if (meridian === 'pm' && hours !== 12) {
      hours += 12;
    } else if (meridian === 'am' && hours === 12) {
      hours = 0;
    }
  }

  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');

  return `${formattedHours}:${formattedMinutes}`;
};

const formatDate = (date: string | null) => {
  if (!date) return '';

  const parsedDate = dayjs(date, 'DD/MM/YYYY');

  if (!parsedDate.isValid()) return '';

  return parsedDate.format('YYYY-MM-DD');
};

import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  useTheme,
} from '@mui/material';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import refresh from 'packages/novous_web/src/assets/icons/Refresh.svg';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import dayjs, { Dayjs } from 'dayjs';
import { useToast } from 'packages/novous_web/src/context/ToastContext';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import useDebounce from 'packages/novous_web/src/hooks/useDebounce';
import AlertToast from 'packages/novous_web/src/common/components/Alert';
import CustomDateTimePicker from 'packages/novous_web/src/common/components/CustomDateTimePicker';
import InputField from 'packages/novous_web/src/common/components/InputField';
import useApiError from 'packages/novous_web/src/api/useApiError';

interface FilterDataModelProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subTitle?: string;
  description?: string;
}

interface FormDataType {
  vehicleId: string;
  cameraId: string;
  // vehicleName: string;
  startDate: string | null;
  endDate: string | null;
  startTime: string | null;
  endTime: string | null;
  selectedOption: string;
  emailIds?: string[];
}

const roundDownTo5 = (date: Dayjs) => {
  const minutes = Math.floor(date.minute() / 5) * 5;
  return date.minute(minutes).second(0).millisecond(0);
};

const now = dayjs();
const end = roundDownTo5(now);
const start = end.subtract(5, 'minute');

const initialDateTime: FormDataType = {
  vehicleId: '',
  cameraId: '04',
  startDate: start.format('DD/MM/YYYY'),
  endDate: end.format('DD/MM/YYYY'),
  startTime: start.format('hh:mm A'),
  endTime: end.format('hh:mm A'),
  selectedOption: '',
};

const RequestVideoPlayback: React.FC<FilterDataModelProps> = ({
  open,
  onClose,
  title = 'Customise Table',
  subTitle = '',
  description = 'Choose which sections to display and arrange them the way you like.',
}) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const theme = useTheme();
  const [formData, setFormData] = useState<FormDataType>(initialDateTime);
  const [formErrors, setFormErrors] = useState<Partial<FormDataType>>({});
  const [cameraList, setCameraList] = useState(null);
  const [regNoValid, setRegNoValid] = useState(false);
  const [emailInputs, setEmailInputs] = useState<string[]>(['']);
  const debouncedVehicleRegistrationNumber = useDebounce(formData.vehicleId, 1000);
  const debouncedVehicleId = useDebounce(formData.vehicleId, 500);
  const [toast, setToast] = useState({
    open: false,
    message: '',
    type: 'error',
  });
  const [demoDateTime, setDemoDateTime] = useState<Dayjs | null>(dayjs());
  const [hasRequested, setHasRequested] = useState(false);

  const {
    data: checkVehicleExistData,
    fetchData: checkVehicleExist,
    loading: vehicleCheckLoading,
    error: vehicleCheckError,
  } = useAxios<any>(`${API_ENDPOINTS.directories.VEHICLE_REGISTRATION_EXIST}/${debouncedVehicleId}`, 'POST');
  const {
    data: addVideoData,
    error: addVideoError,
    loading: addVideoLoading,
    fetchData: postAddVideoData,
  } = useApiError<any>(`${API_ENDPOINTS.directories.ADD_VIDEO}`, 'POST');
  const {
    data: cameraData,
    error: cameraError,
    loading: cameraLoading,
    fetchData: getCameraData,
  } = useAxios<any>(`${API_ENDPOINTS.directories.GET_CAMERA}${formData.vehicleId}`, 'GET');
  const {
    data: vehicleData,
    error: vehicleError,
    loading: vehicleLoading,
    fetchData: fetchVehicleData,
  } = useAxios<any>(`${API_ENDPOINTS.directories.GET_ALL_VEHICLES}`, 'POST');

  useEffect(() => {
    if (vehicleError || cameraError || addVideoError) {
      showToast(vehicleError || cameraError || addVideoError || 'Something went wrong!', 'error');
    }
  }, [vehicleError, cameraError, addVideoError]);
  useEffect(() => {
    if (cameraData?.data) {
      setCameraList(cameraData?.data);
    }
    if (cameraError) {
      setCameraList(null);
    }
  }, [cameraData, cameraError]);

  //  useEffect(() => {
  //         if (checkVehicleExistData && checkVehicleExistData?.data) {
  //             setRegNoValid(false);
  //             setFormErrors((prev) => ({
  //                 ...prev,
  //                 vehicleId: 'Vehicle Already Mapped'
  //             }));
  //         } else {
  //             setRegNoValid(true);
  //         }
  //     }, [checkVehicleExistData, vehicleCheckError]);

  const handleFieldChange = (field: keyof FormDataType, value: string | null) => {
    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));

    setFormErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      if (value) {
        delete newErrors[field];
      }
      return newErrors;
    });
  };

  const addEmailInput = () => {
    if (emailInputs.length < 5) {
      setEmailInputs([...emailInputs, '']);
    } else {
      showToast('Maximum 5 email addresses allowed', 'warning');
    }
  };

  // Remove email input field
  const removeEmailInput = (index: number) => {
    if (emailInputs.length > 1) {
      const updatedEmails = emailInputs.filter((_, i) => i !== index);
      setEmailInputs(updatedEmails);
    }
  };

  // Handle email input change
  const handleEmailChange = (index: number, value: string) => {
    const updatedEmails = [...emailInputs];
    updatedEmails[index] = value;
    setEmailInputs(updatedEmails);
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const domainRegex = /^[a-zA-Z0-9-]+\.(com)$/;

  const validateForm = (): boolean => {
    let errors: Partial<FormDataType> = {};
    let isValid = true;
    if (!formData.vehicleId) {
      errors.vehicleId = 'Vehicle ID is required';
      isValid = false;
    }
    if (!formData.startDate) {
      errors.startDate = 'Start date is required';
      isValid = false;
    }
    if (!formData.endDate) {
      errors.endDate = 'End date is required'; // Fixed: was using startDate for endDate error
      isValid = false;
    }
    if (!formData.startTime) {
      errors.startTime = 'Start time is required'; // Fixed: was using startDate for startTime error
      isValid = false;
    }
    if (!formData.endTime) {
      errors.endTime = 'End time is required'; // Fixed: was using startDate for endTime error
      isValid = false;
    }
    if (!formData.selectedOption) {
      errors.selectedOption = 'Camera is required';
      isValid = false;
    }

    const validEmails: string[] = emailInputs.filter(email => email.trim() !== '');

    if (validEmails.length > 0) {
      const emailErrors: string[] = [];

      validEmails.forEach((email, index) => {
        if (!emailRegex.test(email)) {
          emailErrors.push(`Invalid email at position ${index + 1}`);
        } else {
          const domainPart = email.split('@')[1];
          if (
            domainPart &&
            (!domainRegex.test(domainPart) || domainPart.split('.').length !== 2)
          ) {
            emailErrors.push(`Invalid domain at position ${index + 1}`);
          }
        }
      });

      if (emailErrors.length > 0) {
        errors.emailIds = emailErrors;
        isValid = false;
      }
    }

    if (formData.startDate && formData.endDate) {
      const startDate = dayjs(formData.startDate, 'DD/MM/YYYY');
      const endDate = dayjs(formData.endDate, 'DD/MM/YYYY');

      if (startDate.isAfter(endDate)) {
        errors.startDate = 'End date cannot be before start date';
      }

      if (formData.startTime && formData.endTime) {
        const startDateTime = dayjs(`${formData.startDate} ${formData.startTime}`, 'DD/MM/YYYY hh:mm A');
        const endDateTime = dayjs(`${formData.endDate} ${formData.endTime}`, 'DD/MM/YYYY hh:mm A');
        const now = dayjs().second(0).millisecond(0);

        if (startDate.isSame(endDate, 'day')) {
          if (startDateTime.isSame(endDateTime)) {
            errors.endTime = 'End time cannot be same as start time';
          }
          if (startDateTime.isAfter(endDateTime)) {
            errors.endTime = 'End time cannot be before start time';
          }
        }

        if (endDateTime.isAfter(now)) {
          errors.endTime = 'End time cannot be in the future';
        }

        const diffInMinutes = endDateTime.diff(startDateTime, 'minute');
        if (diffInMinutes > 5) {
          showToast('Start Time and End Time Difference should not be more than 5 minutes', 'error');
          return false;
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleReset = () => {
    setFormData(initialDateTime);
    setCameraList(null);
    setFormErrors({});
  };

  const handleClose = () => {
    setFormData(initialDateTime);
    setFormErrors({});
    setCameraList(null);
    onClose();
  };

  const handleSave = async () => {
    if (validateForm()) {
      setHasRequested(true);
      const validEmails = emailInputs.filter(email => email.trim() !== '');
      const finalEmailValidation = validEmails.every(email => {
        if (!emailRegex.test(email)) return false;

        const domainPart = email.split('@')[1];
        return !(domainPart && (!domainRegex.test(domainPart) || domainPart.split('.').length !== 2));
      });

      if (!finalEmailValidation) {
        setToast({
          open: true,
          message: 'Please fix email validation errors before submitting.',
          type: 'error',
        })
        setHasRequested(false);
        return;
      }
      const payloads = {
        vehicleId: formData.vehicleId,
        cameraId: formData.cameraId,
        selectedOption: formData.selectedOption,
        startTime: formatTime(formData.startTime),
        endTime: formatTime(formData.endTime),
        startDate: formatDate(formData.startDate),
        endDate: formatDate(formData.endDate),
        vodEmailIds: validEmails,
      }
      console.log("payloads", payloads);
      try {
        const response = await postAddVideoData(payloads);
        if (response.status === 201) {
          showToast(response.message, 'success');
        } else {
          showToast(response.message, 'error');
        }
      } catch (error: any) {
        showToast((error as Error).message, 'error');
      } finally {
        setHasRequested(false);
      }
      handleClose();
    }
  };

  useEffect(() => {
    fetchVehicleData({ fleetId: user?.customer_id, lastEvaluatedKey: null });
  }, [user?.customer_id]);
  useEffect(() => {
    if (formData.vehicleId) {
      getCameraData();
    }
  }, [formData.vehicleId]);

  useEffect(() => {
    if (!hasRequested) return;
    if (addVideoData && addVideoData.status === 201) {
      showToast(addVideoData.message, 'success');
      setHasRequested(false);
    } else if (addVideoData && addVideoData.status === 500) {
      showToast(addVideoData.message, 'error');
      setHasRequested(false);
    } else if (addVideoError) {
      showToast(addVideoError, 'error');
      setHasRequested(false);
    }
  }, [addVideoData, addVideoError, hasRequested]);

  const handleCheckboxChange = (value: string) => {
    setFormData((prevState) => ({
      ...prevState,
      cameraId: value,
      selectedOption: prevState.selectedOption === value ? '' : value,
    }));
    setFormErrors((prevErrors) => {
      return {
        ...prevErrors,
        selectedOption: '',
      };
    });
  };

  const handleDateTimeChange = (type: 'start' | 'end', val: string | null) => {
    if (val) {
      const [date, ...timeParts] = val.split(' ');
      const time = timeParts.join(' ');
      handleFieldChange(`${type}Date`, date);
      handleFieldChange(`${type}Time`, time);

      // Adjust the other value to maintain 5 min difference
      const thisDT = dayjs(`${date} ${time}`, 'DD/MM/YYYY hh:mm A');
      if (type === 'start') {
        const newEnd = thisDT.add(5, 'minute');
        handleFieldChange('endDate', newEnd.format('DD/MM/YYYY'));
        handleFieldChange('endTime', newEnd.format('hh:mm A'));
      } else {
        const newStart = thisDT.subtract(5, 'minute');
        handleFieldChange('startDate', newStart.format('DD/MM/YYYY'));
        handleFieldChange('startTime', newStart.format('hh:mm A'));
      }
    } else {
      handleFieldChange(`${type}Date`, null);
      handleFieldChange(`${type}Time`, null);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="customise-table-title"
        aria-describedby="customise-table-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '70%',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            overflowY: 'auto',
            height: '80vh',
          }}
        >
          <div className="flex justify-between h-full flex-col">
            <div className="flex gap-8 flex-col">
              <div className="flex flex-col gap-1">
                <div className="flex justify-between items-center">
                  <Typography variant="h6">
                    {title} {subTitle}
                  </Typography>
                  <CustomIconButton
                    text="Reset All"
                    icon={<img src={refresh} alt="reset" />}
                    background="transparent"
                    variant="outlined"
                    onClick={handleReset}
                  />
                </div>
                {description && (
                  <Typography variant="button" fontWeight={400} color="secondary">
                    {description}
                  </Typography>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <FormControl fullWidth>
                  <CustomDropDownInput
                    label="Vehicle Registration No"
                    isMandatory={true}
                    options={
                      vehicleData?.data?.map((item: any) => ({
                        value: item?.vehicleRegistrationNo,
                        label: item?.vehicleRegistrationNo,
                      })) || []
                    }
                    value={formData.vehicleId}
                    onChange={(v) => handleFieldChange('vehicleId', v)}
                    placeholder="Select Vehicle Registration No."
                    status={'success'}
                  />
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {formErrors.vehicleId}
                  </Typography>
                  {/* <FormHelperText color="error">{formErrors.vehicleId}</FormHelperText> */}
                </FormControl>

                <div className="flex gap-4 sm:flex-row flex-col">
                  <CustomDateTimePicker
                    label="Start Date & Time"
                    value={
                      formData.startDate && formData.startTime
                        ? `${formData.startDate} ${formData.startTime}`
                        : null
                    }
                    onChange={(val) => handleDateTimeChange('start', val)}
                    placement="top"
                    maxPopperHeight="330px"
                    minutesStep={5}
                    maxDateTime={dayjs().format('DD/MM/YYYY hh:mm A')}
                    is12HourFormat
                  />
                  <CustomDateTimePicker
                    label="End Date & Time"
                    value={
                      formData.endDate && formData.endTime ? `${formData.endDate} ${formData.endTime}` : null
                    }
                    onChange={(val) => handleDateTimeChange('end', val)}
                    placement="top"
                    maxPopperHeight="330px"
                    minutesStep={5}
                    maxDateTime={dayjs().format('DD/MM/YYYY hh:mm A')}
                    is12HourFormat
                  />
                </div>
                {(formErrors.startDate ||
                  formErrors.endDate ||
                  formErrors.startTime ||
                  formErrors.endTime) && (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                      {formErrors.endTime || formErrors.startTime || formErrors.endDate || formErrors.startDate}
                    </Typography>
                  )}

                {cameraList && <Typography>Select Camera</Typography>}
                <FormControl component="fieldset" error={!!formErrors.selectedOption}>
                  <FormGroup row>
                    {Object.entries(cameraList ?? {}).map(([key, value]) => {
                      if (!Array.isArray(value) || value.length < 2) return null;
                      const [label, id] = value;
                      return (
                        <FormControlLabel
                          key={key}
                          control={
                            <Checkbox
                              sx={{
                                color: theme.palette.primary.main,
                                '&.Mui-checked': {
                                  color: theme.palette.primary.main,
                                },
                              }}
                              checked={formData.selectedOption === id}
                              onChange={() => handleCheckboxChange(id)}
                            />
                          }
                          label={<Typography sx={{ color: theme.palette.primary.main }}>{label}</Typography>}
                        />
                      );
                    })}
                  </FormGroup>
                  {cameraList && formErrors.selectedOption && (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                      {formErrors.selectedOption}
                    </Typography>
                  )}
                </FormControl>

                {cameraList && formData.selectedOption && (
                  <div className="flex flex-col gap-2 mt-4">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Email Addresses
                      </Typography>
                      {emailInputs.length < 5 && (
                        <CustomIconButton
                          icon={<span style={{ fontSize: '18px', fontWeight: 'bold' }}>+</span>}
                          background="transparent"
                          onClick={addEmailInput}
                          variant="outlined"
                          text=''
                        />
                      )}
                    </Box>

                    <Box className="grid grid-cols-2 gap-2 w-full items-center">
                      {emailInputs.map((email, index) => (
                        <Box key={index} sx={{ display: 'flex', position: 'relative', alignItems: 'center', gap: 1, mb: 1 }}>
                          <InputField
                            label={`Email Address ${index + 1}`}
                            type="email"
                            placeholder="Enter email address"
                            value={email}
                            onChange={(e) => handleEmailChange(index, e.target.value)}
                          />
                          {emailInputs.length > 1 && (
                            <Box className="absolute right-0 top-1/2" sx={{ transform: 'translateY(-18%)' }}>
                              <CustomIconButton
                                icon={<span style={{ fontSize: '18px', fontWeight: 'bold' }}>-</span>}
                                background="transparent"
                                onClick={() => removeEmailInput(index)}
                                variant="outlined"
                                text=''
                              />
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>

                    {formErrors.emailIds && formErrors.emailIds.length > 0 && (
                      <Box>
                        {formErrors.emailIds.map((error, index) => (
                          <Typography key={index} color="error" variant="body2">
                            {error}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    <Typography variant="caption" color="textSecondary">
                      {emailInputs.length}/5 email addresses
                    </Typography>
                  </div>
                )}

              </div>
            </div>

            <div className="flex gap-2 mt-4 items-center justify-between pb-5">
              <CustomIconButton
                text="Cancel"
                background="transparent"
                outline
                padding="0px"
                variant="outlined"
                onClick={handleClose}
              />
              <CustomIconButton text="Apply" outline padding="0px" variant="contained" onClick={handleSave} />
            </div>
          </div>
        </Box>
      </Modal>

      <AlertToast
        open={toast.open}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        type={toast.type as any}
      />
    </>
  );
};

export default RequestVideoPlayback;
